import React from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  OktonInput,
  OktonLoadingState,
  OktonSelect,
  OktonTextarea,
} from "@okton/platform-ui";
import { AiOffer } from "./types";
import { period, price, request } from "./aiPlansApi";
export const AiPlanAdmin: React.FC<{ token: string }> = ({ token }) => {
  const [offers, setOffers] = React.useState<AiOffer[]>([]);
  const [editing, setEditing] = React.useState<AiOffer | null>(null);
  const [amount, setAmount] = React.useState("");
  const [extraAmount, setExtraAmount] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  React.useEffect(() => {
    const controller = new AbortController();
    (async () => {
      await request(
        "/v1/me/context/sso-admin",
        token,
        "GET",
        undefined,
        controller.signal,
      );
      const rows = await request(
        "/v1/admin/ai-plans",
        token,
        "GET",
        undefined,
        controller.signal,
      );
      if (!controller.signal.aborted) setOffers(rows);
    })()
      .catch((reason) => {
        if (!controller.signal.aborted) setError(reason.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token]);
  const update = (key: keyof AiOffer, value: unknown) =>
    setEditing((previous) => (previous ? { ...previous, [key]: value } : null));
  const updateAdditional = (
    key: keyof NonNullable<AiOffer["additional"]>,
    value: number | string,
  ) =>
    setEditing((previous) =>
      previous?.additional
        ? { ...previous, additional: { ...previous.additional, [key]: value } }
        : previous,
    );
  const save = async () => {
    if (!editing || saving) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const additional = editing.additional
        ? {
            ...editing.additional,
            priceCents: Math.round(Number(extraAmount.replace(",", ".")) * 100),
          }
        : null;
      if (
        additional &&
        (!/^[0-9]+([.,][0-9]{1,2})?$/.test(extraAmount) ||
          !Number.isSafeInteger(additional.priceCents) ||
          additional.priceCents < 1 ||
          !Number.isSafeInteger(additional.quantity) ||
          additional.quantity < 1 ||
          !additional.termsVersion.trim())
      )
        throw new Error("Complete quantidade, preço e termos do adicional.");
      const result: AiOffer = await request(
        "/v1/admin/ai-plans",
        token,
        "PUT",
        {
          ...editing,
          additional,
          priceCents:
            amount.trim() === ""
              ? null
              : Math.round(Number(amount.replace(",", ".")) * 100),
        },
      );
      setOffers((previous) =>
        previous.map((offer) => (offer.id === result.id ? result : offer)),
      );
      setEditing(null);
      setNotice(
        "Rascunho salvo. Publique quando os dados comerciais estiverem completos.",
      );
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Não foi possível salvar.",
      );
    } finally {
      setSaving(false);
    }
  };
  if (loading)
    return <OktonLoadingState title="Carregando catálogo IA..." minH="160px" />;
  return (
    <Stack spacing="5">
      <Heading size="lg">Catálogo de planos IA</Heading>
      <Text>
        Preços e franquias são específicos deste módulo. Cobrança automática
        ainda não habilitada.
      </Text>
      <Text>
        Os planos só aparecem para contratação depois de publicados. Configure
        níveis progressivos de preço, franquia e desempenho.
      </Text>
      {error && (
        <Text role="alert" color="red.500">
          {error}
        </Text>
      )}
      {notice && (
        <Text role="status" color="green.600">
          {notice}
        </Text>
      )}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="4">
        {offers.map((offer) => (
          <Box key={offer.id} borderWidth="1px" borderRadius="lg" p="5">
            <Stack spacing="3">
              <Badge>
                {offer.status === "published" ? "Publicado" : "Rascunho"}
              </Badge>
              <Heading size="md">{offer.name}</Heading>
              <Text>{offer.description}</Text>
              <Text>
                {price(offer)} {period(offer)}
              </Text>
              <Text>
                Franquia:{" "}
                {offer.quota === null
                  ? "A definir"
                  : offer.quota + " " + offer.unit}
              </Text>
              <Button
                isDisabled={saving}
                onClick={() => {
                  setEditing({ ...offer });
                  setExtraAmount(
                    offer.additional
                      ? (offer.additional.priceCents / 100).toFixed(2)
                      : "",
                  );
                  setAmount(
                    offer.priceCents === null
                      ? ""
                      : (offer.priceCents / 100).toFixed(2),
                  );
                  setError("");
                  setNotice("");
                }}
              >
                Editar {offer.name}
              </Button>
              <Button
                variant="outline"
                isDisabled={
                  saving || editing !== null || offer.status === "published"
                }
                onClick={async () => {
                  setSaving(true);
                  setError("");
                  try {
                    const result = await request(
                      "/v1/admin/ai-plans/publish",
                      token,
                      "POST",
                      { id: offer.id, revision: offer.revision },
                    );
                    setOffers((previous) =>
                      previous.map((item) =>
                        item.id === result.id ? result : item,
                      ),
                    );
                    setNotice("Oferta publicada.");
                  } catch (reason) {
                    setError(
                      reason instanceof Error
                        ? reason.message
                        : "Não foi possível publicar.",
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Publicar {offer.name}
              </Button>
            </Stack>
          </Box>
        ))}
      </SimpleGrid>
      {editing && (
        <Box borderWidth="1px" borderRadius="lg" p="5">
          <Stack spacing="4">
            <Heading size="md">Editar {editing.name}</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
              <OktonInput
                name="ai-plan-name"
                label="Nome"
                value={editing.name}
                onChange={(event) => update("name", event.target.value)}
              />
              <OktonInput
                name="ai-plan-price"
                label="Preço comercial em reais"
                aria-label="Preço comercial em reais"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="A definir"
              />
              <OktonInput
                name="ai-plan-quota"
                label="Franquia"
                type="number"
                min="1"
                value={editing.quota ?? ""}
                onChange={(event) =>
                  update(
                    "quota",
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              />
              <OktonInput
                name="ai-plan-unit"
                label="Unidade da franquia"
                value={editing.unit}
                onChange={(event) => update("unit", event.target.value)}
                placeholder="Ex.: solicitações"
              />
              <OktonSelect
                name="ai-plan-period"
                label="Período"
                value={editing.period}
                onChange={(event) => update("period", event.target.value)}
              >
                <option value="month">Mensal</option>
                <option value="accumulated">Total acumulado</option>
              </OktonSelect>
              <OktonInput
                name="ai-plan-performance-rank"
                label="Nível de desempenho"
                type="number"
                min="1"
                value={editing.performanceRank ?? ""}
                onChange={(event) =>
                  update(
                    "performanceRank",
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              />
              <OktonInput
                name="ai-plan-performance-profile"
                label="Descrição do desempenho"
                value={editing.performanceProfile}
                onChange={(event) =>
                  update("performanceProfile", event.target.value)
                }
              />
              <OktonInput
                name="ai-plan-terms-version"
                label="Versão dos termos"
                value={editing.termsVersion}
                onChange={(event) => update("termsVersion", event.target.value)}
              />
            </SimpleGrid>
            <OktonTextarea
              name="ai-plan-description"
              label="Descrição do plano"
              value={editing.description}
              onChange={(event) => update("description", event.target.value)}
            />
            <OktonTextarea
              name="ai-plan-features"
              label="Recursos (um por linha)"
              value={editing.features.join("\n")}
              onChange={(event) =>
                update("features", event.target.value.split("\n"))
              }
            />
            <Box borderWidth="1px" borderRadius="md" p="4">
              <Stack spacing="3">
                <Checkbox
                  isChecked={!!editing.additional}
                  isDisabled={saving}
                  onChange={(event) =>
                    update(
                      "additional",
                      event.target.checked
                        ? { quantity: 0, priceCents: 0, termsVersion: "" }
                        : null,
                    )
                  }
                >
                  Oferecer pacote adicional
                </Checkbox>
                <Text fontSize="sm">
                  Disponível em “Adicionar mais” para filiais previamente
                  autorizadas. Não altera a mensalidade nesta etapa.
                </Text>
                {editing.additional && (
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="3">
                    <OktonInput
                      name="ai-plan-additional-quantity"
                      label="Quantidade do adicional"
                      aria-label="Quantidade do adicional"
                      type="number"
                      min="1"
                      value={editing.additional.quantity || ""}
                      onChange={(event) =>
                        updateAdditional("quantity", Number(event.target.value))
                      }
                    />
                    <OktonInput
                      name="ai-plan-additional-price"
                      label="Preço do adicional em reais"
                      aria-label="Preço do adicional em reais"
                      inputMode="decimal"
                      value={extraAmount}
                      onChange={(event) => setExtraAmount(event.target.value)}
                    />
                    <OktonInput
                      name="ai-plan-additional-terms"
                      label="Termos do adicional"
                      aria-label="Termos do adicional"
                      value={editing.additional.termsVersion}
                      onChange={(event) =>
                        updateAdditional("termsVersion", event.target.value)
                      }
                    />
                  </SimpleGrid>
                )}
              </Stack>
            </Box>
            <Text fontSize="sm">
              Salvar coloca a oferta em rascunho. Contratos anteriores mantêm as
              condições já aceitas.
            </Text>
            <Stack direction="row">
              <Button
                colorScheme="blue"
                isDisabled={saving}
                onClick={() => save()}
              >
                Salvar rascunho
              </Button>
              <Button isDisabled={saving} onClick={() => setEditing(null)}>
                Cancelar
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
